/** Row from `GET /agent/runs` — flattened `agent_run` plus deduction snapshot fields. */
export interface AgentRun {
  id: string
  org_id: string
  deduction_id: string
  job_id: string | null
  created_at: string
  completed_at: string | null
  action_taken: string
  action_payload: Record<string, unknown>
  reasoning: string | null
  system_prompt: string | null
  user_prompt: string | null
  response_text: string | null
  prompt_tokens: number | null
  completion_tokens: number | null
  cost_usd: number | null
  model: string | null
  dry_run: boolean
  success: boolean
  error: string | null
  rating: string | null
  rating_created_at: string | null
  rating_user_id: string | null
  human_feedback: string | null
  rerun_of_run_id: string | null
  triggered_by_user_id: string | null
  manual_decision_executed_at: string | null
  custom_prompt: string | null
  batch_id: string | null
  custom_context: Record<string, boolean> | null
  /**
   * Action kinds that actually ran (post-gating), e.g. ["categorize"].
   * Null on runs that never executed, and on runs executed before this was
   * recorded — see `executed_action_kinds`, which distinguishes the two.
   */
  executed_actions: string[] | null
  invoice_number: string
  deduction_amount_usd: number
  category_name: string
  category_description: string | null
  status_value: string
}

/** Which flavor of wizard produced a run. */
export type WizRunKind = "background" | "bigwiz" | "customwiz"

/** CustomWiz runs carry the user's prompt; BigWiz/manual runs carry a triggering user; background runs carry neither. */
export function run_kind(run: Pick<AgentRun, "custom_prompt" | "triggered_by_user_id">): WizRunKind {
  if (run.custom_prompt) return "customwiz"
  if (run.triggered_by_user_id) return "bigwiz"
  return "background"
}

export type WizReviewState = "pending_review" | "executed" | "failed" | "informational"

type ReviewStateFields = Pick<AgentRun, "dry_run" | "success" | "action_taken" | "manual_decision_executed_at">

/** Actionable state of a run: does it still need human review, was it applied, did it fail, or is it skip-only? */
export function run_review_state(run: ReviewStateFields): WizReviewState {
  if (!run.success || run.action_taken === "error") return "failed"
  if (run.manual_decision_executed_at || !run.dry_run) return "executed"
  const actions = run.action_taken.split(",").filter(a => a.trim() !== "")
  const has_executable = actions.some((a: string) => a !== "skip")
  return has_executable ? "pending_review" : "informational"
}

/**
 * Which action kinds actually ran, or `null` when that is genuinely unknown.
 *
 * Null is NOT "nothing ran" — runs executed before `executed_actions` was
 * recorded have no per-action detail, and `action_taken` on those is the
 * model's *proposal*, not the reviewer's selection. Ticking those would claim
 * actions ran that the reviewer deselected, so callers must render no
 * per-action state at all when this returns null.
 */
export function executed_action_kinds(
  run: Pick<AgentRun, "executed_actions" | "dry_run" | "manual_decision_executed_at">
): string[] | null {
  if (Array.isArray(run.executed_actions)) return run.executed_actions
  return null
}

/**
 * Actions a run applied on its own while it still awaits review — BigWiz auto-applies
 * `categorize` for every brand. Empty once the run has been reviewed (its executed set
 * then covers them) or when nothing was applied.
 */
export function auto_applied_kinds(
  run: Pick<AgentRun, "executed_actions" | "dry_run" | "manual_decision_executed_at">
): string[] {
  if (run_was_executed(run) || !Array.isArray(run.executed_actions)) return []
  return run.executed_actions
}

/** True when this run applied something (so the UI can show executed state at all). */
export function run_was_executed(run: Pick<AgentRun, "dry_run" | "manual_decision_executed_at">): boolean {
  return !run.dry_run || run.manual_decision_executed_at != null
}

/**
 * Whether a run's suggested actions are still reviewable and runnable.
 *
 * Deliberately takes no user/role: reviewing the wizard is not an admin power, and the
 * server agrees (`can_use_wizard` in server/src/agent/mod.rs gates every wizard endpoint
 * on "signed in", with org scoping as the real boundary). Gating this on `role` is what
 * left plain `user`s able to run BigWiz but unable to act on anything it suggested — the
 * buttons simply weren't rendered for them.
 */
export function can_execute_run(run: ReviewStateFields): boolean {
  return run.dry_run && run.success && run.action_taken !== "error" && !run.manual_decision_executed_at
}

/**
 * Newest first — for the deduction Wizard panel, where the first run is the
 * headline and the rest are collapsed history.
 *
 * Deliberately NOT "pending review first": that floated a stale older proposal
 * above the run the user had just executed, so the thing they just did appeared
 * to vanish down the list. The newest run is the one that reflects the
 * deduction's current state; older pending runs keep their "Needs review" badge
 * in the collapsed history.
 */
export function sort_runs_for_display<T extends Pick<AgentRun, "created_at">>(runs: T[]): T[] {
  return [...runs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

export function format_agent_run_deduction_amount(amount: number | undefined): string {
  if (amount === undefined || Number.isNaN(amount)) {
    return "—"
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

export function format_action_payload_json(payload: unknown): string {
  if (payload === undefined || payload === null || (typeof payload === "object" && Object.keys(payload as object).length === 0)) {
    return "—"
  }
  return JSON.stringify(payload, null, 2)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("format_agent_run_deduction_amount", () => {
    it("formats USD", () => {
      expect(format_agent_run_deduction_amount(123.45)).toMatch(/\$123\.45/)
    })
    it("handles missing", () => {
      expect(format_agent_run_deduction_amount(undefined)).toBe("—")
    })
  })

  describe("format_action_payload_json", () => {
    it("pretty-prints objects", () => {
      expect(format_action_payload_json({ action: "skip" })).toContain('"action"')
    })
    it("returns em dash for empty object", () => {
      expect(format_action_payload_json({})).toBe("—")
    })
  })

  describe("run_kind", () => {
    it("custom_prompt wins over triggered_by_user_id", () => {
      expect(run_kind({ custom_prompt: "check these", triggered_by_user_id: "u1" })).toBe("customwiz")
    })
    it("triggered_by_user_id without prompt is bigwiz", () => {
      expect(run_kind({ custom_prompt: null, triggered_by_user_id: "u1" })).toBe("bigwiz")
    })
    it("neither is background", () => {
      expect(run_kind({ custom_prompt: null, triggered_by_user_id: null })).toBe("background")
    })
  })

  describe("run_review_state", () => {
    const base = { dry_run: true, success: true, action_taken: "categorize", manual_decision_executed_at: null }
    it("failed when not success or error action", () => {
      expect(run_review_state({ ...base, success: false })).toBe("failed")
      expect(run_review_state({ ...base, action_taken: "error" })).toBe("failed")
    })
    it("executed when manually executed or non-dry-run success", () => {
      expect(run_review_state({ ...base, manual_decision_executed_at: "2026-07-01T00:00:00Z" })).toBe("executed")
      expect(run_review_state({ ...base, dry_run: false })).toBe("executed")
    })
    it("pending_review for dry-run success with executable actions", () => {
      expect(run_review_state(base)).toBe("pending_review")
      expect(run_review_state({ ...base, action_taken: "skip,categorize" })).toBe("pending_review")
    })
    it("informational for skip-only dry runs", () => {
      expect(run_review_state({ ...base, action_taken: "skip" })).toBe("informational")
    })
  })

  describe("can_execute_run", () => {
    const base = { dry_run: true, success: true, action_taken: "categorize", manual_decision_executed_at: null }

    it("is true for a pending dry run — for every user, with no role input", () => {
      // Regression: a brand user with role "user" saw the wizard's suggestion but no
      // "Run selected actions" button, so BigWiz was unusable for them.
      expect(can_execute_run(base)).toBe(true)
    })

    it("is false once already executed, failed, or error", () => {
      expect(can_execute_run({ ...base, manual_decision_executed_at: "2026-06-01T00:00:00Z" })).toBe(false)
      expect(can_execute_run({ ...base, success: false })).toBe(false)
      expect(can_execute_run({ ...base, action_taken: "error" })).toBe(false)
      expect(can_execute_run({ ...base, dry_run: false })).toBe(false)
    })
  })

  describe("sort_runs_for_display", () => {
    it("sorts newest first", () => {
      const old_run = { created_at: "2026-01-01T00:00:00Z" }
      const mid_run = { created_at: "2026-05-01T00:00:00Z" }
      const new_run = { created_at: "2026-06-01T00:00:00Z" }
      expect(sort_runs_for_display([old_run, new_run, mid_run])).toEqual([new_run, mid_run, old_run])
    })

    it("keeps the just-executed run on top even when an older run still needs review", () => {
      // The regression this ordering exists to prevent: executing the newest run
      // used to push it below a stale pending proposal.
      const pending_old = { created_at: "2026-01-01T00:00:00Z", dry_run: true, success: true, action_taken: "categorize", manual_decision_executed_at: null }
      const executed_new = { created_at: "2026-06-01T00:00:00Z", dry_run: true, success: true, action_taken: "categorize", manual_decision_executed_at: "2026-06-01T01:00:00Z" }
      expect(sort_runs_for_display([pending_old, executed_new])[0]).toBe(executed_new)
    })
  })

  describe("executed_action_kinds", () => {
    it("returns the recorded kinds", () => {
      expect(
        executed_action_kinds({ executed_actions: ["categorize", "match_promotions"], dry_run: true, manual_decision_executed_at: "2026-06-01T00:00:00Z" })
      ).toEqual(["categorize", "match_promotions"])
    })

    it("returns null (unknown) for legacy executed runs rather than guessing", () => {
      // action_taken on these is the model's proposal, so ticking from it would
      // claim actions ran that the reviewer deselected.
      expect(executed_action_kinds({ executed_actions: null, dry_run: false, manual_decision_executed_at: null })).toBeNull()
      expect(executed_action_kinds({ executed_actions: null, dry_run: true, manual_decision_executed_at: "2026-06-01T00:00:00Z" })).toBeNull()
    })

    it("distinguishes 'nothing ran' from 'unknown'", () => {
      expect(executed_action_kinds({ executed_actions: [], dry_run: true, manual_decision_executed_at: "2026-06-01T00:00:00Z" })).toEqual([])
    })
  })

  describe("run_was_executed", () => {
    it("is true for auto-run and manually executed runs only", () => {
      expect(run_was_executed({ dry_run: false, manual_decision_executed_at: null })).toBe(true)
      expect(run_was_executed({ dry_run: true, manual_decision_executed_at: "2026-06-01T00:00:00Z" })).toBe(true)
      expect(run_was_executed({ dry_run: true, manual_decision_executed_at: null })).toBe(false)
    })
  })

  describe("WizardRunsSection card timestamp format", () => {
    it("renders 12-hour US format with AM/PM (not 24-hour)", async () => {
      const { format, parseISO } = await import("date-fns")
      // The format string the card uses — keep this assertion in lock-step
      // with WizardRunsSection.tsx so a regression to `HH:mm` is caught.
      const out = format(parseISO("2026-05-26T16:56:00Z"), "MMM d, h:mm a")
      expect(out).toMatch(/\d{1,2}:\d{2} (AM|PM)$/)
      expect(out).not.toMatch(/\b16:56\b/)
    })
  })

  it("auto_applied_kinds reports what a pending run already applied", () => {
    const pending = { dry_run: true, manual_decision_executed_at: null }
    expect(auto_applied_kinds({ ...pending, executed_actions: ["categorize"] })).toEqual(["categorize"])
    expect(auto_applied_kinds({ ...pending, executed_actions: null })).toEqual([])
    // once reviewed, the executed set is the record — nothing is "automatic" any more
    expect(auto_applied_kinds({ dry_run: true, manual_decision_executed_at: "2026-09-28T00:00:00Z", executed_actions: ["categorize"] })).toEqual([])
  })
}
